/**
 * AI Analysis Panel Component
 * TASK-213: Build Transaction Detail Modal
 *
 * REUSABLE component for displaying AI risk analysis
 * Will be reused in TASK-215 (Media Monitoring) and other compliance views
 *
 * @component
 * @example
 * <AIAnalysisPanel
 *   riskScore={78}
 *   riskLevel="high"
 *   flags={flags}
 *   recommendation="Trigger EDD workflow"
 * />
 */

import React, { useMemo } from 'react';
import { Sparkles, FileText, CheckSquare } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { RiskLevel, TransactionFlag } from '../../types/compliance';
import FlagsList from './FlagsList';

interface AIAnalysisPanelProps {
  /** Risk score (0-100) */
  riskScore: number;
  /** Risk level category */
  riskLevel: RiskLevel;
  /** List of detected flags */
  flags: TransactionFlag[];
  /** AI recommendation text */
  recommendation: string;
  /** Documents required for compliance */
  documentsRequired?: string[];
  /** Additional checks to perform */
  additionalChecks?: string[];
  /** Contextual note about the analysis */
  contextualNote?: string;
}

const AIAnalysisPanel: React.FC<AIAnalysisPanelProps> = ({
  riskScore,
  riskLevel,
  flags,
  recommendation,
  documentsRequired = [],
  additionalChecks = [],
  contextualNote,
}) => {
  // Get color scheme based on risk level
  const riskColors = useMemo(() => {
    const colors = {
      low: {
        bg: 'bg-green-50',
        text: 'text-green-800',
        border: 'border-green-200',
        progress: 'bg-green-500',
      },
      medium: {
        bg: 'bg-yellow-50',
        text: 'text-yellow-800',
        border: 'border-yellow-200',
        progress: 'bg-yellow-500',
      },
      high: {
        bg: 'bg-orange-50',
        text: 'text-orange-800',
        border: 'border-orange-200',
        progress: 'bg-orange-500',
      },
      critical: {
        bg: 'bg-red-50',
        text: 'text-red-800',
        border: 'border-red-200',
        progress: 'bg-red-500',
      },
    };
    return colors[riskLevel];
  }, [riskLevel]);

  // Format risk level for display
  const riskLevelDisplay = riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1);

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-900">AI Risk Analysis</h3>
        </div>

        {/* Risk Score Visualization */}
        <div className={`rounded-lg p-4 border ${riskColors.bg} ${riskColors.border} mb-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${riskColors.text}`}>
              Risk Score: {riskScore}/100
            </span>
            <span className={`text-sm font-bold ${riskColors.text}`}>
              {riskLevelDisplay} Risk
            </span>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-white/50 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${riskColors.progress}`}
              style={{ width: `${riskScore}%` }}
            />
          </div>
        </div>

        {/* Flags List */}
        {flags.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              ⚠️ Flags Detected ({flags.length})
            </h4>
            <FlagsList flags={flags} />
          </div>
        )}

        {/* AI Recommendation */}
        <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <h4 className="text-sm font-semibold text-indigo-900 mb-2">
            🤖 AI Recommendation
          </h4>
          <p className="text-sm text-indigo-800">{recommendation}</p>
        </div>

        {/* Documents Required */}
        {documentsRequired.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1 mb-2">
              <FileText className="w-4 h-4" />
              Documents Required ({documentsRequired.length})
            </h4>
            <ul className="space-y-1">
              {documentsRequired.map((doc, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-gray-400 mt-0.5">•</span>
                  <span>{doc}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Additional Checks */}
        {additionalChecks && additionalChecks.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1 mb-2">
              <CheckSquare className="w-4 h-4" />
              Additional Checks Required ({additionalChecks.length})
            </h4>
            <ul className="space-y-1">
              {additionalChecks.map((check, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-gray-400 mt-0.5">•</span>
                  <span>{check}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contextual Note */}
        {contextualNote && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Context:</span> {contextualNote}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIAnalysisPanel;
